import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type CartItems } from '@/scripts/cart/cart-items'
import { type CartDrawerItems } from '@/scripts/cart/cart-drawer-items'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	addItemsToCart,
	configPostRequest,
	getCart,
	getPersonalizationCartComponents,
} from '@/scripts/core/cart-functions'

export class CartRemoveButton extends UcoastEl {
	static htmlSelector = 'cart-remove-button'
	constructor() {
		super()

		this.addEventListener('click', this.removeItem.bind(this))
	}

	removeItem() {
		const cartItems =
			q.oc<CartItems>(this, 'cart-items') ||
			q.rc<CartDrawerItems>(this, 'cart-drawer-items')
		const line = q.ra(this, 'data-index')
		const groupItemKeys = q.oa(this, 'data-group-item-keys')
		const personalizationGroupId = q.oa(
			this,
			'data-personalization-group-id'
		)

		if (personalizationGroupId) {
			void this.removePersonalization(
				cartItems,
				line,
				personalizationGroupId
			)
			return
		}

		if (groupItemKeys) {
			const updates = groupItemKeys
				.split(',')
				.map((key) => key.trim())
				.filter(Boolean)
				.reduce<Record<string, number>>((acc, key) => {
					acc[key] = 0
					return acc
				}, {})

			cartItems.updateItemsByKey(line, updates)
			return
		}

		cartItems.updateQuantity(line, '0')
	}

	private async removePersonalization(
		cartItems: CartItems | CartDrawerItems,
		line: string,
		groupId: string
	) {
		try {
			const [cart, components] = await Promise.all([
				getCart(),
				getPersonalizationCartComponents(),
			])
			if (!('items' in cart)) throw new Error(cart.description)
			const component = components.find(
				(item) => item.groupId === groupId
			)
			const parent = cart.items.find(
				(item) => item.key === component?.parentKey
			)
			if (!component || !parent) throw new Error(window.cartStrings.error)
			const child = component.childKey
				? (cart.items.find((item) => item.key === component.childKey) ??
					(component.childVariantId > 0 && component.childQuantity > 0
						? {
								key: component.childKey,
								variant_id: component.childVariantId,
								quantity: component.childQuantity,
								properties: {
									...component.properties,
									_customization_group_id: groupId,
								},
							}
						: undefined))
				: undefined
			if (component.childKey && !child)
				throw new Error(window.cartStrings.error)

			let parentKey = parent.key
			if (component.childKey) {
				const removeResponse = await fetch(
					window.routes.cart_change_url,
					configPostRequest({ id: component.childKey, quantity: 0 })
				).then((response) => response.json())
				if (removeResponse.status)
					throw new Error(removeResponse.description)
				let remainingParent = removeResponse.items?.find(
					(item: { key: string; properties?: Record<string, string> }) =>
						item.key === parentKey ||
						item.properties?._customization_group_id === groupId
				)
				if (!remainingParent) {
					const refreshedCart = await getCart()
					if (!('items' in refreshedCart))
						throw new Error(refreshedCart.description)
					remainingParent = refreshedCart.items.find(
						(item) =>
							item.key === parentKey ||
							item.properties?._customization_group_id === groupId
					)
				}
				if (!remainingParent) throw new Error(window.cartStrings.error)
				parentKey = remainingParent.key
			}

			const properties = {
				...Object.fromEntries(
					Object.entries(parent.properties ?? {}).filter(
						([key]) =>
							!key.startsWith('_personalization_') &&
							key !== '_customization_group_id'
					)
				),
				_plain_pair_id: groupId,
			}
			const sections = cartItems
				.getSectionsToRender()
				.map((section) => section.section)
				.filter((section): section is string => Boolean(section))
			const restoreChild = async (cause: unknown) => {
				if (!child) return
				const restoreResponse = await addItemsToCart([
					{
						id: child.variant_id,
						quantity: child.quantity,
						parent_line_key: parentKey,
						properties: child.properties,
					},
				], sections)
				if ('status' in restoreResponse)
					throw new Error(restoreResponse.description, { cause })
				if (!('sections' in restoreResponse))
					throw new Error(window.cartStrings.error, { cause })
				const restoredItems = cart.items.some(
					(item) => item.key === child.key
				)
					? cart.items
					: [...cart.items, child]
				cartItems.renderParsedState(
					{
						...restoreResponse,
						item_count: restoredItems.reduce(
							(total, item) => total + item.quantity,
							0
						),
						items: restoredItems,
					},
					{
						line,
						itemsCount: document.querySelectorAll(
							cartItems.instanceSelectors.item
						).length,
					}
				)
			}
			let parentResponse
			try {
				parentResponse = await fetch(
					window.routes.cart_change_url,
					configPostRequest({
						id: parentKey,
						quantity: parent.quantity,
						properties,
						sections,
						sections_url: window.location.pathname,
					})
				).then((response) => response.json())
			} catch (error) {
				const reconciledCart = await getCart()
				if ('items' in reconciledCart && child) {
					const reconciledParent = reconciledCart.items.find(
						(item) =>
							item.key === parentKey ||
							item.properties?._customization_group_id === groupId ||
							item.properties?._plain_pair_id === groupId
					)
					const childStillExists = reconciledCart.items.some(
						(item) =>
							item.variant_id === child.variant_id &&
							item.properties?._customization_group_id === groupId
					)
					if (
						reconciledParent?.properties?._customization_group_id ===
							groupId &&
						!childStillExists
					)
						await restoreChild(error)
				}
				throw error
			}
			if (parentResponse.status) {
				const error = new Error(parentResponse.description)
				await restoreChild(error)
				throw error
			}
			cartItems.renderParsedState(parentResponse, {
				line,
				itemsCount: document.querySelectorAll(
					cartItems.instanceSelectors.item
				).length,
			})
		} catch (error) {
			console.error(error)
			const errors = q.os<HTMLElement>(cartItems.instanceSelectors.errors)
			if (errors) errors.textContent = window.cartStrings.error
		}
	}
}
